import { publicHref } from '../lib/publicUrls';
import './InquiryPages.css';
import { Check, ExternalLink, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ProgramJourney from '../components/ProgramJourney';
import SEO from '../components/SEO';
import { ServiceHero } from '../components/ServiceHeader';
import { MEDIA_SLOTS } from '../lib/mediaTruth';

const brentwoodPlans = [
  {
    name: 'Single',
    monthly: '$85/month',
    paidInFull: '$900/year',
    enrollment: '$50 enrollment fee',
    annualFee: '$69 annual fee on monthly plan',
    applications: [
      {
        label: 'Join Month-to-Month',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=883&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-single-monthly',
      },
      {
        label: 'Join Paid in Full',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=888&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-single-pif',
      },
    ],
  },
  {
    name: 'Couple',
    monthly: '$150/month',
    paidInFull: '$1,600/year',
    enrollment: '$60 enrollment fee',
    annualFee: '$89 annual fee on monthly plan',
    applications: [
      {
        label: 'Join Month-to-Month',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=884&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-couple-monthly',
      },
      {
        label: 'Join Paid in Full',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=889&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-couple-pif',
      },
    ],
  },
  {
    name: 'Family of 3',
    monthly: '$220/month',
    paidInFull: '$2,400/year',
    enrollment: '$70 enrollment fee',
    annualFee: '$109 annual fee on monthly plan',
    applications: [
      {
        label: 'Join Month-to-Month',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=885&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-family-3-monthly',
      },
      {
        label: 'Join Paid in Full',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=890&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-family-3-pif',
      },
    ],
  },
  {
    name: 'Family of 4',
    monthly: '$250/month',
    paidInFull: '$2,800/year',
    enrollment: '$70 enrollment fee',
    annualFee: '$109 annual fee on monthly plan',
    applications: [
      {
        label: 'Join Month-to-Month',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=886&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-family-4-monthly',
      },
      {
        label: 'Join Paid in Full',
        url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=891&ProspectID=&SecondID=&HomeClub=1&PromoCode=&tab=1',
        testId: 'join-brentwood-family-4-pif',
      },
    ],
  },
];

const discoveryBayPlans = [
  {
    name: 'Single',
    price: '$75/month',
    url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=853&ProspectID=&SecondID=&HomeClub=2&PromoCode=&tab=1',
    testId: 'join-discovery-bay-single',
  },
  {
    name: 'Couple',
    price: '$130/month',
    url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=854&ProspectID=&SecondID=&HomeClub=2&PromoCode=&tab=1',
    testId: 'join-discovery-bay-couple',
  },
  {
    name: 'Family',
    price: '$180/month',
    url: 'https://deltavac.clubautomation.com/membership-application?iframe=on&MembershipID=855&ProspectID=&SecondID=&HomeClub=2&PromoCode=&tab=1',
    testId: 'join-discovery-bay-family',
  },
];

const membershipFaqs = [
  ['Can I access the Discovery Bay location?', 'Yes. Call the membership team at (925) 240-2990 to confirm the membership option that includes the access you need.'],
  ['Which classes have an additional charge?', 'MX4 and Reformer Pilates are additional-charge programs with certified coaches and personalized instruction.'],
  ['Do you offer childcare?', 'Yes. Childcare is available for an additional fee while account holders are using the club. Family Time is available from 12:00pm–2:00pm daily for children accompanied by an adult with an active account; Family Time use is limited to the basketball court and ping-pong table.'],
  ["Do you offer children's swim lessons?", 'Yes. Delta Valley offers swim instruction from 6 months old through adulthood.'],
  ['What amenities are included?', 'Amenities include private locker rooms, showers, saunas, steam rooms, an indoor heated lap pool, a kickboxing studio, and more.'],
  ['What classes are included?', 'Delta Valley publishes more than 50 group exercise classes each week. Check the live schedule for current classes and instructors.'],
  ['Are you hiring?', 'If you’re interested in joining the team, send your resume to info@deltavac.com.'],
  ['Do you offer corporate memberships?', 'Yes. Call (925) 240-2990 to ask about options for local businesses.'],
];

const EnrollmentButton = ({ application, variant = 'default' }) => (
  <Button
    asChild
    variant={variant}
    className={variant === 'default'
      ? 'w-full bg-[#0072BB] hover:bg-[#005A94]'
      : 'w-full'}
  >
    <a
      href={publicHref(application.url)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={application.testId}
      aria-label={`${application.label} for ${application.planName || 'Delta Valley'}`}
    >
      {application.label}
      <ExternalLink size={15} className="ml-2" aria-hidden="true" />
    </a>
  </Button>
);

const MembershipPage = () => (
  <div className="dv-inquiries dv-inquiries--membership" data-testid="membership-page">
    <SEO
      title="Gym Memberships | Brentwood & Discovery Bay | Delta Valley"
      description="View membership options and apply online for Delta Valley Health Club in Brentwood and Discovery Bay, California."
      keywords="Brentwood gym membership, Discovery Bay gym membership, Delta Valley membership pricing"
    />

    <ServiceHero
      id="membership-title"
      className="dv-service-hero--photo-right"
      media={MEDIA_SLOTS.membershipHero}
      haze="218 156 44"
      parent={{ label: 'Home', href: '/' }}
      current="Membership"
      title="Join the Club"
      lede="Choose your club and complete your membership application online."
      actions={(
        <>
          <a className="dv-button dv-button--light" href={publicHref('/book-a-tour')}>Book a Tour</a>
          <a className="dv-button dv-button--outline-light" href="#membership-inquiry">Request Membership Information</a>
        </>
      )}
      facts={['Brentwood & Discovery Bay', 'Monthly or yearly plans', 'Secure online application']}
    />

    <section className="dv-inquiries__section">
      <div className="container-custom">
        <div className="dv-inquiries__section-heading mb-10">
          <p className="uppercase tracking-[0.2em] text-sm font-semibold text-[#0072BB] mb-2">Brentwood</p>
          <h2 className="dv-size-heading text-3xl md:text-4xl font-bold mb-3">Current Membership Pricing</h2>
          <p className="dv-size-body text-gray-600">Choose an available plan below to continue to Delta Valley's secure membership application.</p>
        </div>
        <div className="dv-inquiries__plans">
          {brentwoodPlans.map((plan) => (
            <article key={plan.name} className="dv-inquiries__plan">
              <div className="dv-inquiries__plan-inner">
                <h3 className="dv-size-subheading text-2xl font-bold mb-5">{plan.name}</h3>
                <div className="dv-size-subheading text-2xl font-bold text-[#0072BB] mb-1">{plan.monthly}</div>
                <div className="text-gray-600 mb-5">or {plan.paidInFull}</div>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="dv-size-body flex gap-2"><Check size={17} className="text-green-600 shrink-0 mt-0.5" />{plan.enrollment}</li>
                  <li className="dv-size-body flex gap-2"><Check size={17} className="text-green-600 shrink-0 mt-0.5" />{plan.annualFee}</li>
                </ul>
                <div className="mt-auto pt-6 space-y-2">
                  {plan.applications.map((application, index) => (
                    <EnrollmentButton
                      key={application.url}
                      application={{ ...application, planName: `${plan.name} membership` }}
                      variant={index === 0 ? 'default' : 'outline'}
                    />
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-6">Pricing reflects the current published Brentwood club rates and is subject to confirmation during enrollment.</p>
      </div>
    </section>

    <section className="dv-inquiries__section" data-testid="discovery-bay-memberships">
      <div className="container-custom">
        <div className="dv-inquiries__section-heading mb-10">
          <p className="uppercase tracking-[0.2em] text-sm font-semibold text-[#0072BB] mb-2">Discovery Bay</p>
          <h2 className="dv-size-heading text-3xl md:text-4xl font-bold mb-3">Discovery Bay-Only Memberships</h2>
          <p className="dv-size-body text-gray-600">Select a membership type to view current terms and complete the secure application for Home Club 2.</p>
        </div>
        <div className="dv-inquiries__plans dv-inquiries__plans--discovery">
          {discoveryBayPlans.map((plan) => (
            <article key={plan.name} className="dv-inquiries__plan">
              <div className="dv-inquiries__plan-inner">
                <h3 className="dv-size-subheading text-2xl font-bold mb-3">{plan.name}</h3>
                <p className="dv-size-subheading text-2xl font-bold text-[#0072BB] mb-2">{plan.price}</p>
                <p className="dv-size-body text-gray-600 mb-6">Discovery Bay-only month-to-month membership.</p>
                <div className="mt-auto">
                  <EnrollmentButton
                    application={{
                      label: 'Join Discovery Bay',
                      url: plan.url,
                      testId: plan.testId,
                      planName: `${plan.name} membership`,
                    }}
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="dv-inquiries__section">
      <div className="container-custom">
        <div className="mb-9">
          <p className="uppercase tracking-[0.2em] text-sm font-semibold text-[#0072BB] mb-2">Before You Join</p>
          <h2 className="dv-size-heading text-3xl md:text-4xl font-bold">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {membershipFaqs.map(([question, answer]) => (
            <details key={question} className="dv-inquiries__faq group">
              <summary className="cursor-pointer list-none font-semibold text-gray-900">{question}</summary>
              <p className="dv-size-body pt-3 leading-relaxed text-gray-600">{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section className="dv-inquiries__section dv-inquiries__highlights">
      <div className="container-custom grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="dv-size-heading text-3xl font-bold mb-5">Membership Highlights</h2>
          <ul className="space-y-3 text-gray-700">
            {['Indoor heated lap pool', 'Locker rooms, showers, saunas, and steam rooms', 'Childcare available for an additional fee', 'Personal training, swim lessons, MX4, and Reformer Pilates options'].map((item) => (
              <li key={item} className="dv-size-body flex gap-2"><Check className="text-green-600 shrink-0" size={20} />{item}</li>
            ))}
          </ul>
        </div>
        <Card><CardContent className="p-6">
          <h2 className="dv-size-subheading text-2xl font-bold mb-3">Prefer to talk first?</h2>
          <p className="dv-size-body text-gray-600 mb-5">Our membership team can answer questions about plan details and current terms.</p>
          <Button asChild className="w-full bg-[#0072BB] hover:bg-[#005A94]"><a href="tel:+19252402990"><Phone size={18} className="mr-2" />Call (925) 240-2990</a></Button>
        </CardContent></Card>
      </div>
    </section>

    <ProgramJourney
      id="membership-inquiry" testId="membership-inquiry-journey" photo="home-tour"
      source="membership-page" location="Brentwood"
      eyebrow="Membership inquiry"
      title={['Request membership information.', 'Your request, in about a minute.']}
      lede="Share your contact information and interests so the right team member can follow up."
      planTitle="Your membership request" request={['Club', 'Brentwood · 120 Guthrie Ln.']}
      reviewNoun="membership request" startLabel="Start My Request" buttonText="Send My Request"
      successTitle="Request Received"
      successMessage="Our Brentwood membership team will contact you with current options and next steps."
    />
  </div>
);

export default MembershipPage;
